// /apps/patient/src/routes/index.tsx

import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { usePatient } from '../contexts/PatientContext';
import { useState, useEffect } from 'react';
import { PatientLayout } from '../components/layout/PatientLayout';
import { FeatureGuard } from '../components/navigation/FeatureGuard';
import { isOnboardingComplete, getExerciseAccessType, hasExerciseAccess } from '../utils/onboarding';
import { getCurrentPlatform } from '../config/platforms';

// Pages
import { LoginPage } from '../pages/Login';
import { SignupPage } from '../pages/Signup';
import { Dashboard } from '../pages/Dashboard';
import { CalendarView } from '../pages/calendar/CalendarView';
import { FormResponseList } from '../pages/forms/FormResponseList';
import { FormResponseDetail } from '../pages/forms/FormResponseDetail';
import { PrescriptionsList } from '../pages/prescriptions/PrescriptionsList';
import { PrescriptionDetail } from '../pages/prescriptions/PrescriptionDetail';
import { EncountersList } from '../pages/records/EncountersList';
import { EncounterDetail } from '../pages/records/EncounterDetail';
import MessageInbox from '../pages/messages/MessageInbox';
import MessageCompose from '../pages/messages/MessageCompose';
import { ProgramsList } from '../pages/programs/ProgramsList';
import { ProgramDetail } from '../pages/programs/ProgramDetail';
import { SessionPlayer } from '../pages/programs/SessionPlayer';
import { PersonalizePlanPage } from '../pages/programs/PersonalizePlanPage';
import { SessionLogView } from '../pages/programs/SessionLogView';
import { JoinPage } from '../pages/JoinPage';
import { AppointmentsList } from '../pages/appointments/AppointmentsList';
import { AppointmentDetail } from '../pages/appointments/AppointmentDetail';
import { AppointmentRequest } from '../pages/appointments/AppointmentRequest';
import { AppointmentReschedule } from '../pages/appointments/AppointmentReschedule';
import { ManageAppointmentPage } from '../pages/appointments/ManageAppointmentPage';
import { ClaimOfferPage } from '../pages/appointments/ClaimOfferPage';
import { Profile } from '../pages/Profile';
import { SubscribeRequired } from '../pages/SubscribeRequired';
import { MyLocations } from '../pages/settings/MyLocations';
import { VideoRoom } from '../components/video/VideoRoom';
import { PatientOnboarding } from '../pages/PatientOnboarding';

import { InvoicesList } from '../pages/billing/InvoicesList';
import { InvoiceDetail } from '../pages/billing/InvoiceDetail';
import { PaymentPage } from '../pages/billing/PaymentPage';
import { PaymentSuccess } from '../pages/billing/PaymentSuccess';
import { PaymentMethods } from '../pages/billing/PaymentMethods';

// Progress pages
import { ProgressPage } from '../pages/progress/ProgressPage';
import { SessionDetailView } from '../pages/progress/SessionDetailView';

// Class pages
import { ClassBrowser } from '../pages/classes/ClassBrowser';
import { ClassDetail } from '../pages/classes/ClassDetail';
import { MyClasses } from '../pages/classes/MyClasses';

// Public pages
import { WorkoutPlanPreview } from '../pages/programs/WorkoutPlanPreview';
import { SuperbillView } from '../pages/billing/SuperbillView';

// Placeholder pages
const Forms = FormResponseList;


// Protected Route with platform-aware onboarding check
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { patient, loading, isAuthenticated } = usePatient();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Capture any query params before redirecting
    const programId = searchParams.get('program_id');
    if (programId) {
      sessionStorage.setItem('pending_program_id', programId);
    }
    const workoutPlanId = searchParams.get('workout_plan_id');
    if (workoutPlanId) {
      sessionStorage.setItem('pending_workout_plan_id', workoutPlanId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!isAuthenticated && !loading) {
      navigate("/login", { state: { returnTo: location.pathname }, replace: true });
      return;
    }

    // Use platform-aware onboarding check
    if (isAuthenticated && patient && !isOnboardingComplete(patient) && location.pathname !== '/onboarding') {
      navigate("/onboarding", { replace: true });
      return;
    }

    // Standalone Ready2Exercise users need an active subscription (trial/active/access code) past onboarding.
    // Profile stays reachable so they can manage the subscription; /subscribe is the resubscribe page.
    if (
      isAuthenticated && patient &&
      getCurrentPlatform().id === 'exercise' &&
      isOnboardingComplete(patient) &&
      getExerciseAccessType(patient) === 'standalone' &&
      !hasExerciseAccess(patient) &&
      !['/subscribe', '/profile', '/onboarding'].includes(location.pathname)
    ) {
      navigate("/subscribe", { replace: true });
      return;
    }
  }, [isAuthenticated, patient, loading, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
};

const AppRouter = () => {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/plan/:id" element={<WorkoutPlanPreview />} />
        <Route path="/join/:code" element={<JoinPage />} />
        <Route path="/routine/:key" element={<WorkoutPlanPreview kind="routine" />} />
        <Route path="/superbill" element={<SuperbillView />} />
        <Route path="/a/:token" element={<ManageAppointmentPage />} />
        <Route path="/w/:token" element={<ClaimOfferPage />} />

        {/* Onboarding Route - Outside of Layout */}
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <PatientOnboarding />
          </ProtectedRoute>
        } />


        {/* Protected Routes with Layout */}
        <Route
          element={
            <ProtectedRoute>
              <PatientLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />

          <Route path="/calendar" element={<CalendarView />} />

          {/* Clinical Records Routes */}
          <Route
            path="/records"
            element={
              <FeatureGuard feature="clinical_records">
                <EncountersList />
              </FeatureGuard>
            }
          />

          <Route
            path="/records/:id"
            element={
              <FeatureGuard feature="clinical_records">
                <EncounterDetail />
              </FeatureGuard>
            }
          />
          {/* Prescription Routes */}
          <Route
            path="/prescriptions"
            element={
              <FeatureGuard feature="prescriptions">
                <PrescriptionsList />
              </FeatureGuard>
            }
          />

          <Route
            path="/prescriptions/:id"
            element={
              <FeatureGuard feature="prescriptions">
                <PrescriptionDetail />
              </FeatureGuard>
            }
          />
          
          {/* Program Routes - Updated to also check workout_plans */}
          <Route path="/programs" element={
            <FeatureGuard feature="workout_plans" fallbackFeature="standalone_exercises">
              <ProgramsList />
            </FeatureGuard>
          } />

          <Route path="/programs/:id" element={
            <FeatureGuard feature="workout_plans">
              <ProgramDetail />
            </FeatureGuard>
          } />

          <Route path="/programs/:id/personalize" element={
            <FeatureGuard feature="workout_plans">
              <PersonalizePlanPage />
            </FeatureGuard>
          } />

          {/* Session Routes */}
          <Route path="/programs/:assignmentId/session/:date" element={
            <FeatureGuard feature="workout_plans">
              <SessionPlayer />
            </FeatureGuard>
          } />

          <Route path="/programs/:assignmentId/log/:date" element={
            <FeatureGuard feature="workout_plans">
              <SessionLogView />
            </FeatureGuard>
          } />

          {/* Progress Routes */}
          <Route path="/progress" element={
            <FeatureGuard feature="workout_plans">
              <ProgressPage />
            </FeatureGuard>
          } />
          <Route path="/progress/session/:sessionId" element={
            <FeatureGuard feature="workout_plans">
              <SessionDetailView />
            </FeatureGuard>
          } />

          {/* Appointment Routes */}
          <Route
            path="/appointments"
            element={
              <FeatureGuard feature="appointments">
                <AppointmentsList />
              </FeatureGuard>
            }
          />

          <Route
            path="/appointments/request"
            element={
              <FeatureGuard feature="appointments">
                <AppointmentRequest />
              </FeatureGuard>
            }
          />

          <Route
            path="/appointments/:id"
            element={
              <FeatureGuard feature="appointments">
                <AppointmentDetail />
              </FeatureGuard>
            }
          />

          <Route path="/appointments/:id/video" element={
            <FeatureGuard feature="appointments">
              <VideoRoom />
            </FeatureGuard>
          } />

          <Route
            path="/appointments/reschedule/:id"
            element={
              <FeatureGuard feature="appointments">
                <AppointmentReschedule />
              </FeatureGuard>
            }
          />

          {/* Class Routes */}
          <Route path="/classes" element={
            <FeatureGuard feature="classes">
              <ClassBrowser />
            </FeatureGuard>
          } />
          <Route path="/classes/:id" element={
            <FeatureGuard feature="classes">
              <ClassDetail />
            </FeatureGuard>
          } />
          <Route path="/my-classes" element={
            <FeatureGuard feature="classes">
              <MyClasses />
            </FeatureGuard>
          } />

          { /* Messaging Routes */}
          <Route
            path="/messages"
            element={
              <FeatureGuard feature="messaging">
                <MessageInbox />
              </FeatureGuard>
            }
          />
          <Route
            path="/messages/compose"
            element={
              <FeatureGuard feature="messaging">
                <MessageCompose />
              </FeatureGuard>
            }
          />

          <Route
            path="/forms/response/:id"
            element={
              <FeatureGuard feature="forms">
                <FormResponseDetail />
              </FeatureGuard>
            }
          />

          <Route
            path="/forms"
            element={
              <FeatureGuard feature="forms">
                <Forms />
              </FeatureGuard>
            }
          />

          {/* Billing Routes */}
          <Route path="/billing" element={<InvoicesList />} />
          <Route path="/billing/invoices/:id" element={<InvoiceDetail />} />
          <Route path="/billing/pay/:id" element={<PaymentPage />} />
          <Route path="/billing/payment-success/:id" element={<PaymentSuccess />} />
          <Route path="/billing/payment-methods" element={<PaymentMethods />} />
          <Route path="/billing/payment" element={<PaymentPage />} />

          {/* Subscription Route - NEW for exercise platform */}
          <Route path="/subscription" element={<InvoicesList />} />

          <Route path="/profile" element={<Profile />} />
          <Route path="/subscribe" element={<SubscribeRequired />} />

          {/* Settings Routes */}
          <Route path="/settings/locations" element={<MyLocations />} />
        </Route>

        {/* Catch all - redirect based on onboarding status */}
        <Route path="*" element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
};

export default AppRouter;